import { AxiosRequestConfig, AxiosResponse } from 'axios'

/**
 * 响应结果
 */
enum ResponseStatusTypes {
  /**
   * 请求响应正常
   */
  Pass,
  /**
   * 请求Token过期
   */
  Expires,
}

declare namespace Pai {
  /**
   * process注入
   */
  type process = {
    env: {
      PackageJSON: any
    }
  }
  /**
   * 登录信息
   */
  type LoginInfo = {
    /**
     * token（加密）
     */
    st?: string
    /**
     * 登录时间戳
     */
    t?: number
    [index: string]: string | number | undefined
  }

  /**
   * 缓存工具
   * / Cache storage
   */
  interface CacheStorage {
    /**
     * 获取缓存
     * @param key
     */
    getItem(key: string): string | null
    /**
     * 设置缓存
     * @param key
     * @param value
     */
    setItem(key: string, value: string): void
    /**
     * 清除缓存
     * @param key
     */
    removeItem(key: string): void
  }

  /**
   * AuthApi 配置项
   */
  type AuthApiOptions = {
    /**
     * 源地址（协议+域名+端口）
     * / 主要用于跳转登录
     */
    origin?: string
    /**
     * 接口前置地址
     * / 主要用于满足生产环境的接口请求的代理
     */
    baseURL?: string | 'https://dev.k8s.flyrise.cn' | 'https://pai.flyrise.cn' | 'https://sit.flyrise.cn'
    /**
     * 从登录中心重定向回来后，是否清空url中的查询参数
     */
    redirectQueryClear?: boolean
    /**
     * 从登录中心重定向回来后，清空url中的哪些参数
     * / 默认['st']
     */
    redirectQueryClearKeys?: string[]
    /**
     * 缓存key
     * / 默认为自动生成
     */
    cacheKey?: string
    /**
     * 客户端类型
     */
    type?: 'web' | 'workbench' | 'developer' | 'console' | 'operate'
    /**
     * 缓存工具
     * / Cache storage
     */
    cacheStorage?: CacheStorage
    /**
     * 是否开启混淆
     */
    obfuscate?: boolean
    /**
     * token传递名
     */
    tokenKey?: string
    /**
     * 登录路径
     */
    loginPath?: string
    /**
     * 是否携带回调地址
     */
    redirect?: boolean
    /**
     * 检查从URL进行认证的key，URl存在该key则从url获取
     */
    checkAuthKeyByUrl?: any
    paiAuthByUrl?: boolean
  }

  /**
   * 确认设置
   */
  interface ConfirmOptions {
    /**
     * 是否启用确认框
     */
    confirm?: boolean
    /**
     * 确认框回调
     */
    confirmCallback?(): Promise<boolean>
  }

  /**
   * 登出设置
   */
  interface LogoutOptions extends ConfirmOptions {
    /**
     * 操作完成后，是否跳转登录
     */
    stay?: boolean
    /**
     * 指定回调地址
     */
    redirectUrl?: string
  }

  /**
   * 获取Token设置
   */
  interface GetTokenOptions extends LogoutOptions {
    /**
     * 获取失败（包括token为空），是否跳转登录
     */
    stay?: boolean
  }

  /**
   * 获取用户信息设置
   */
  interface GetUserInfoOptions extends LogoutOptions {
    /**
     * 获取失败（包括token为空），是否跳转登录
     */
    stay?: boolean
  }

  /**
   * 用户信息
   */
  type UserInfo = {
    userId: string
    deptId: string
    userName: string
    nickName: string
    userType: string
    email: string
    phonenumber: string
    sex: string
    avatar: string
    isAdmin: number
    entEntity: {
      id: string
      code: string
      name: string
      activate: string
      logo: string
      type: string
      parkId: string
      opEntGroupId: string
    }
    staffEntity: {
      staffId: string
      staffUsId: string
      staffName: string
      staffDeptId: string
      staffDeptName: string
      staffPostId: string
      staffNo: string
      staffHiredate: string
      staffType: string
      staffPhone: string
      staffEmail: string
      staffSex: string
      staffHead: string
    }
    parkEntity: { id: string; code: string; name: string; activate: string; logo: string }
  }

  /**
   * 工具接口
   */
  interface AuthApiInterface {
    /**
     * 登陆
     */
    login(opts?: ConfirmOptions): boolean | Promise<boolean>
    /**
     * 登出（比login多一步：清缓存、token失效）
     */
    logout(opts?: LogoutOptions): boolean | Promise<boolean>
    /**
     * 获取token
     */
    getToken(opts?: GetTokenOptions): string | Promise<string>
    /**
     * 获取用户信息
     */
    getUserInfo(opts?: GetUserInfoOptions): Promise<UserInfo | null | void>
  }

  /**
   * 请求配置
   */
  type AuthRequestConfig = {
    /**
     * 若使用的不是Axios，尝试处理headers（当作对象处理 ）
     */
    headers: any
  }

  /**
   * 响应
   */
  type AuthResponse = {
    /**
     * 若使用的不是Axios，尝试处理data.code（当作对象处理 ）
     */
    data: any
  }

  /**
   * 针对Axios拦截器的处理
   */
  interface AuthInterceptorsInterface {
    /**
     * 处理请求
     */
    requestHandler(req: AxiosRequestConfig | AuthRequestConfig): Promise<void>
    /**
     * 处理响应
     */
    responseHandler(res: AxiosResponse | AuthResponse): ResponseStatusTypes | Promise<ResponseStatusTypes>
  }
}

export default Pai

export { ResponseStatusTypes }
